import { Box, Button, Stack, Tooltip, Typography } from "@mui/material";
import getIcon from "../../../utils/getIcon";
import { useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ecoisAPI from "../../../api/ecois";
import dayjs from "dayjs";
import { HistoryChart } from "../../components/historyChart";
import getTooltipTitle from "../../../utils/getTooltip";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import biosenseAPI from "../../../api/biosense";

import moment from "moment";

export default function HistoryScreen({ historyProps }) {
  const [selectedTypes, setSelectedTypes] = useState([]);
  const { t } = useTranslation();
  const today = new Date(historyProps.latest ? historyProps.latest : null);
  const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 3600 * 1000);
  const [startDate, setStartDate] = useState(sevenDaysAgo);
  const [endDate, setEndDate] = useState(
    new Date(historyProps.latest ? historyProps.latest : null),
  );
  const [chartData, setChartData] = useState(null);
  const handleSelectTypes = (type) => {
    // Allow single type
    setSelectedTypes([type]);
    // Allow multiple types
    // if (selectedTypes.includes(type)) {
    //   setSelectedTypes(selectedTypes.filter((x) => x !== type));
    // } else {
    //   setSelectedTypes((prev) => [...prev, type]);
    // }
  };
  const handleGetHistory = () => {
    Swal.fire("請等候伺服器回傳...");
    Swal.showLoading();
    if (historyProps.seriesAbbr === "esp32s3") {
      biosenseAPI
        .getHistoryValueByTimeRangeAndTypes({
          sn: historyProps.sn,
          from: dayjs(startDate).format("YYYYMMDD"),
          to: dayjs(endDate).format("YYYYMMDD"),
          types: selectedTypes,
        })
        .then((res) => {
          if (res?.error) {
            Swal.fire(
              "錯誤",
              `<div style='flex flex-start'>錯誤信息: ${res.error} ${res.data}</div>`,
              "error",
            );
            return;
          }

          setChartData(getProcessData(res));
          Swal.close();
        });
    } else {
      ecoisAPI
        .getSensorValueByTimeRangeAndTypes({
          sn: historyProps.sn,
          from: dayjs(startDate).format("YYYYMMDD"),
          to: dayjs(endDate).format("YYYYMMDD"),
          types: selectedTypes,
        })
        .then((res) => {
          if (res?.error) {
            Swal.fire(
              "錯誤",
              `<div style='flex flex-start'>錯誤信息: ${res.error} ${res.data}</div>`,
              "error",
            );
            return;
          }
          setChartData(getProcessData(res));
          Swal.close();
        });
    }
  };
  return (
    <>
      <Box
        display='flex'
        flexDirection='column'
        sx={{ backgroundColor: "white", borderRadius: 2, padding: 1, gap: 2 }}
      >
        <Box display='flex' flexDirection='column' gap={2}>
          <Box display='flex'>
            <Typography width='11em'>{t("history.serialnumber")}:</Typography>
            <Typography>{historyProps.sn}</Typography>
          </Box>
          <Box display='flex'>
            <Typography width='11em'>{t("history.latestrecordedtime")}:</Typography>
            <Typography>{historyProps.latest}</Typography>
          </Box>
        </Box>
        <Typography>{t("history.choosesensortypes")}:</Typography>
        <Box display='flex' justifyContent='left'>
          <Stack
            spacing={{ xs: 2, sm: 1 }}
            direction='row'
            justifyContent='left'
            useFlexGap
            flexWrap='wrap'
            width='80%'
          >
            {historyProps.availableTypes.map((type, idx) => (
              <Tooltip title={getTooltipTitle(type, t)} key={idx}>
                <Button
                  key={type}
                  variant='outlined'
                  color={selectedTypes.includes(type) ? "warning" : "inherit"}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "4px",
                    border: "1px solid",
                    backgroundColor: "white",
                    minWidth: 0,
                    "& .MuiButton-startIcon": { margin: 0 },
                  }}
                  startIcon={getIcon(type)}
                  onClick={() => {
                    handleSelectTypes(type);
                  }}
                />
              </Tooltip>
            ))}
          </Stack>
        </Box>
        <Box display='flex' flexDirection='column' gap={2}>
          <Box display='flex' gap={1}>
            <Typography width='7em'>{t("history.startdate")}:</Typography>
            <DatePicker
              showIcon
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              selectsStart
              dateFormat={"dd/MM/yyyy"}
              startDate={startDate}
              endDate={endDate}
            />
          </Box>
          <Box display='flex' gap={1}>
            <Typography width='7em'>{t("history.enddate")}:</Typography>
            <DatePicker
              showIcon
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              selectsEnd
              dateFormat={"dd/MM/yyyy"}
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
            />
          </Box>
        </Box>
        <Box display='flex' flexDirection='row' gap={1}>
          <Button
            fullWidth
            variant='contained'
            color='error'
            disabled={!selectedTypes.length}
            onClick={() => setSelectedTypes([])}
          >
            {t("history.resetsensortypes")}
          </Button>
          <Button
            fullWidth
            variant='contained'
            color='error'
            onClick={() => {
              setStartDate(sevenDaysAgo);
              setEndDate(today);
            }}
          >
            {t("history.resetdates")}
          </Button>
        </Box>
        <Button
          variant='contained'
          color='success'
          disabled={!selectedTypes.length}
          onClick={() => {
            handleGetHistory();
          }}
        >
          {t("history.gethistorydata")}
        </Button>
      </Box>

      {chartData === null ? (
        <></>
      ) : (
        <Box
          minHeight='500px'
          paddingRight='10px'
          marginTop='10px'
          sx={{ backgroundColor: "white", borderRadius: 5 }}
        >
          <HistoryChart chartData={chartData} />
        </Box>
      )}
    </>
  );
}

function getProcessData(originalData) {
  const groupedData = {};
  originalData["list"].forEach((item) => {
    // 将时间转换为每五分钟一组
    const dateTime = moment(item.datetime)
      .startOf("minute")
      .subtract(moment(item.datetime).minute() % 5, "minutes")
      .format("YYYY-MM-DD HH:mm");
    if (!groupedData[dateTime]) {
      groupedData[dateTime] = {
        datetime: item.datetime,
        timestamp: item.timestamp,
        values: {},
      };
    }
    // 动态获取属性名并处理
    const surfaceProps = item.values["地表"];
    for (const prop in surfaceProps) {
      if (!groupedData[dateTime].values[prop]) {
        groupedData[dateTime].values[prop] = [];
      }
      groupedData[dateTime].values[prop].push(parseFloat(surfaceProps[prop]));
    }
  });

  const processedData = {
    total: originalData.total,
    list: Object.values(groupedData).map((group) => {
      const newValues = {};
      for (const prop in group.values) {
        const avgValue =
          group.values[prop].reduce((acc, val) => acc + val, 0) / group.values[prop].length;
        newValues[prop] = avgValue;
      }
      group.values = newValues;
      return group;
    }),
  };
  return processedData;
}
